import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgRole } from "@/hooks/useOrgRole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Building2, Users, DollarSign, Shield, ArrowRight } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

export default function AdminPanel() {
  const { role, isLoading: roleLoading } = useOrgRole();

  const { data: orgs, isLoading } = useQuery({
    queryKey: ["admin-orgs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, plan, billing_status, trial_ends_at, created_at, owner_id")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
    enabled: role === "superadmin" || role === "owner",
  });

  const { data: memberCounts } = useQuery({
    queryKey: ["admin-member-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("org_members")
        .select("org_id");
      if (error) throw error;
      const counts: Record<string, number> = {};
      data?.forEach((m) => {
        counts[m.org_id] = (counts[m.org_id] ?? 0) + 1;
      });
      return counts;
    },
    enabled: role === "superadmin" || role === "owner",
  });

  if (roleLoading) return <div className="p-8 text-muted-foreground">Carregando...</div>;

  // Only superadmin can access this
  if (role !== "superadmin") {
    return <Navigate to="/dashboard" replace />;
  }

  const totalOrgs = orgs?.length ?? 0;
  const totalMembers = Object.values(memberCounts ?? {}).reduce((a, b) => a + b, 0);
  const activeOrgs = orgs?.filter((o) => o.billing_status === "active" || o.billing_status === "trialing").length ?? 0;

  const statusColor = (s: string) => {
    switch (s) {
      case "active": return "default";
      case "trialing": return "secondary";
      case "past_due": return "destructive";
      case "suspended": return "destructive";
      default: return "outline";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-headline font-bold flex items-center gap-2">
          <Shield className="w-6 h-6 text-primary" />
          Painel Admin
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Visão geral de todas as organizações</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Organizações</CardTitle>
            <Building2 className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOrgs}</div>
            <p className="text-xs text-muted-foreground">{activeOrgs} ativas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Membros Total</CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMembers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">MRR Estimado</CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">Conecte billing para calcular</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickAdminCard title="Alerts & SLOs" description="Acompanhar burn rate, alertas abertos e catálogo SLO." to="/admin/alerts-slos" />
        <QuickAdminCard title="System Health" description="Readiness, providers, flags ativas e visão consolidada." to="/admin/system-health" />
        <QuickAdminCard title="Flags & Rollout" description="Acompanhar gates, smoke tests, calendário e rollback." to="/sistema/flags" />
        <QuickAdminCard title="Outbound Chunks" description="Confirmação (i/N) de partes enviadas ao WhatsApp e retry." to="/admin/outbound-chunks" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organizações</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Membros</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs?.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{org.plan ?? "free"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusColor(org.billing_status)}>
                        {org.billing_status}
                      </Badge>
                    </TableCell>
                    <TableCell>{memberCounts?.[org.id] ?? 0}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(org.created_at ?? "").toLocaleDateString("pt-BR")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QuickAdminCard({ title, description, to }: { title: string; description: string; to: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{description}</p>
        <Link to={to}>
          <Button variant="outline" size="sm" className="w-full justify-between">
            Abrir
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
